import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Leaf, X, Download, Gift as GiftIcon, Sparkles, Loader2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { TREES } from './TreeOptions';
import { PLANS, ADDONS, getPlan, getAddon, formatINR } from '../lib/plans';
import { startRazorpayPayment, fetchPaymentConfig } from '../lib/razorpay';
import { toast } from 'sonner';

const OCCASIONS = [
  'Birthday', 'Anniversary', 'Baby birth', 'In memory', 'Wedding', 'Corporate', 'Just because'
];

export default function GiftDialog({ open, onOpenChange, defaultTree, defaultPlan }) {
  const [step, setStep] = React.useState(0); // 0 plan, 1 tree, 2 details, 3 thanks
  const [plan, setPlan] = React.useState(defaultPlan || 'lifetime');
  const [tree, setTree] = React.useState(defaultTree || 'mango');
  const [senderName, setSenderName] = React.useState('');
  const [senderEmail, setSenderEmail] = React.useState('');
  const [senderPhone, setSenderPhone] = React.useState('');
  const [recipient, setRecipient] = React.useState('');
  const [recipientEmail, setRecipientEmail] = React.useState('');
  const [occasion, setOccasion] = React.useState('Birthday');
  const [message, setMessage] = React.useState('');
  const [addonKeys, setAddonKeys] = React.useState([]);
  const [certificateId, setCertificateId] = React.useState(() => Math.random().toString(36).slice(2, 8).toUpperCase());
  const [orderId, setOrderId] = React.useState(null);
  const [paying, setPaying] = React.useState(false);
  const [payConfig, setPayConfig] = React.useState({ configured: false, test_mode: false });

  React.useEffect(() => { if (defaultTree) setTree(defaultTree); }, [defaultTree]);
  React.useEffect(() => { if (defaultPlan) setPlan(defaultPlan); }, [defaultPlan]);

  React.useEffect(() => {
    if (open) {
      setStep(defaultPlan ? 1 : 0);
      fetchPaymentConfig().then(setPayConfig);
    }
  }, [open, defaultPlan]);

  const treeObj = TREES.find((t) => t.key === tree) || TREES[0];
  const planObj = getPlan(plan);
  const selectedAddons = addonKeys.map(getAddon).filter(Boolean);
  const addonTotal = selectedAddons.reduce((sum, a) => sum + a.price, 0);
  const grandTotal = planObj.price + addonTotal;
  const toggleAddon = (key) =>
    setAddonKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  const canProceed = recipient.trim().length > 0 && senderName.trim().length > 0;

  const handlePayAndGenerate = async () => {
    if (!canProceed || paying) return;

    // If payment gateway isn't configured, show the certificate anyway
    // (so the UI is demo-able before real keys are added).
    if (!payConfig.configured) {
      toast.info("Payment gateway not configured — showing certificate in preview mode.");
      setStep(3);
      return;
    }

    setPaying(true);
    const result = await startRazorpayPayment({
      order: {
        plan_key: plan,
        tree_key: tree,
        sender_name: senderName,
        recipient_name: recipient,
        occasion,
        message,
        addon_keys: addonKeys,
        sender_email: senderEmail,
        recipient_email: recipientEmail,
        sender_phone: senderPhone,
      },
      prefill: { name: senderName, email: senderEmail, contact: senderPhone },
    });
    setPaying(false);

    if (result.ok) {
      setOrderId(result.order_id);
      setCertificateId(result.order_id.slice(0, 8).toUpperCase());
      toast.success("Payment received. Your tree is on its way 🌱");
      setStep(3);
    } else {
      toast.error(result.error || 'Payment could not be completed.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} modal={false}>
      <DialogContent
        className="max-w-3xl p-0 overflow-hidden border-brand-line bg-brand-bg rounded-3xl max-h-[92vh] overflow-y-auto"
        data-testid="gift-dialog"
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Gift a tree</DialogTitle>
          <DialogDescription>Dedicate a tree to someone special.</DialogDescription>
        </DialogHeader>

        {/* Progress strip */}
        <div className="flex items-center gap-2 px-8 pt-7 sticky top-0 bg-brand-bg z-10">
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className={`h-[3px] flex-1 rounded-full transition-all duration-500 ${
                i <= step ? 'bg-brand-forest' : 'bg-brand-line'
              }`}
            />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {step === 0 && (
            <motion.div
              key="step-plan"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
              className="px-8 py-8"
            >
              <p className="text-brand-gold tracking-[0.25em] uppercase text-[11px] mb-3">Step 1 · Plan</p>
              <h3 className="font-serif text-3xl md:text-4xl text-brand-ink font-light leading-tight">
                How deep a <em className="text-brand-forest">legacy?</em>
              </h3>

              <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {PLANS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setPlan(p.key)}
                    data-testid={`dialog-plan-${p.key}`}
                    className={`text-left rounded-2xl p-5 border transition-all ${
                      plan === p.key
                        ? 'border-brand-forest ring-2 ring-brand-forest/25 bg-brand-bg'
                        : 'border-brand-line hover:border-brand-gold/60 bg-brand-bg2'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-2xl">{p.icon}</div>
                      <div className="font-serif text-xl text-brand-ink">{formatINR(p.price)}</div>
                    </div>
                    <div className="font-serif text-lg text-brand-ink leading-tight">{p.name}</div>
                    <div className="text-brand-gold italic font-serif text-xs mb-2">{p.tagline}</div>
                    <div className="text-brand-ink2 text-[12px] leading-relaxed line-clamp-2">{p.for}</div>
                    <div className="text-[10px] tracking-[0.2em] uppercase text-brand-ink2 mt-3">{p.duration}</div>
                  </button>
                ))}
              </div>

              <div className="mt-8 flex items-center justify-between">
                <div className="text-sm text-brand-ink2">
                  Selected: <span className="font-serif text-lg text-brand-forest italic">{planObj.name}</span>
                </div>
                <button
                  onClick={() => setStep(1)}
                  data-testid="dialog-next-button"
                  className="rounded-full bg-brand-forest text-brand-bg px-7 py-3 text-sm tracking-wide font-medium hover:bg-brand-ink transition-all"
                >
                  Continue →
                </button>
              </div>
            </motion.div>
          )}

          {step === 1 && (
            <motion.div
              key="step-tree"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
              className="px-8 py-8"
            >
              <p className="text-brand-gold tracking-[0.25em] uppercase text-[11px] mb-3">Step 2 · Tree</p>
              <h3 className="font-serif text-3xl md:text-4xl text-brand-ink font-light leading-tight">
                Which tree will carry <em className="text-brand-forest">their name?</em>
              </h3>

              <div className="mt-7 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2.5 max-h-[46vh] overflow-y-auto pr-1">
                {TREES.map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setTree(t.key)}
                    data-testid={`dialog-tree-${t.key}`}
                    className={`rounded-xl overflow-hidden border text-left transition-all ${
                      tree === t.key
                        ? 'border-brand-forest ring-2 ring-brand-forest/30'
                        : 'border-brand-line hover:border-brand-gold/60'
                    }`}
                  >
                    <div className="aspect-square overflow-hidden bg-brand-bg2">
                      <img src={t.img} alt={t.name} loading="lazy" className="w-full h-full object-cover" />
                    </div>
                    <div className="px-2 py-1.5 bg-brand-bg">
                      <div className="font-serif text-[13px] text-brand-ink leading-tight line-clamp-1">{t.name}</div>
                    </div>
                  </button>
                ))}
              </div>

              <div className="mt-8 flex items-center justify-between">
                <button
                  onClick={() => setStep(0)}
                  data-testid="dialog-back-button"
                  className="text-brand-ink2 hover:text-brand-ink text-sm"
                >
                  ← Back
                </button>
                <button
                  onClick={() => setStep(2)}
                  data-testid="dialog-tree-next-button"
                  className="rounded-full bg-brand-forest text-brand-bg px-7 py-3 text-sm tracking-wide font-medium hover:bg-brand-ink transition-all"
                >
                  Continue →
                </button>
              </div>
            </motion.div>
          )}

          {step === 2 && (
            <motion.div
              key="step-details"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
              className="px-8 py-8"
            >
              <p className="text-brand-gold tracking-[0.25em] uppercase text-[11px] mb-3">Step 3 · Dedicate</p>
              <h3 className="font-serif text-3xl md:text-4xl text-brand-ink font-light leading-tight">
                A few words that <em className="text-brand-forest">will outlive us.</em>
              </h3>

              <div className="mt-7 grid md:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="sender" className="text-brand-ink2 mb-2 block text-xs tracking-widest uppercase">From</Label>
                  <Input
                    id="sender"
                    data-testid="input-sender-name"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    placeholder="Your name"
                    className="bg-brand-bg2 border-brand-line rounded-xl h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="recipient" className="text-brand-ink2 mb-2 block text-xs tracking-widest uppercase">For</Label>
                  <Input
                    id="recipient"
                    data-testid="input-recipient-name"
                    value={recipient}
                    onChange={(e) => setRecipient(e.target.value)}
                    placeholder="Recipient's name"
                    className="bg-brand-bg2 border-brand-line rounded-xl h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-brand-ink2 mb-2 block text-xs tracking-widest uppercase">Your email</Label>
                  <Input
                    id="email"
                    type="email"
                    data-testid="input-sender-email"
                    value={senderEmail}
                    onChange={(e) => setSenderEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="bg-brand-bg2 border-brand-line rounded-xl h-12"
                  />
                </div>
                <div>
                  <Label htmlFor="phone" className="text-brand-ink2 mb-2 block text-xs tracking-widest uppercase">Your phone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    data-testid="input-sender-phone"
                    value={senderPhone}
                    onChange={(e) => setSenderPhone(e.target.value)}
                    placeholder="+91 98765 43210"
                    className="bg-brand-bg2 border-brand-line rounded-xl h-12"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="recip-email" className="text-brand-ink2 mb-2 block text-xs tracking-widest uppercase">Recipient's email <span className="text-brand-ink2/60 normal-case italic">(optional — we'll email their certificate too)</span></Label>
                  <Input
                    id="recip-email"
                    type="email"
                    data-testid="input-recipient-email"
                    value={recipientEmail}
                    onChange={(e) => setRecipientEmail(e.target.value)}
                    placeholder="recipient@example.com"
                    className="bg-brand-bg2 border-brand-line rounded-xl h-12"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-brand-ink2 mb-2 block text-xs tracking-widest uppercase">Occasion</Label>
                  <div className="flex flex-wrap gap-2">
                    {OCCASIONS.map((o) => (
                      <button
                        key={o}
                        type="button"
                        onClick={() => setOccasion(o)}
                        data-testid={`dialog-occasion-${o.toLowerCase().replace(/\s+/g, '-')}`}
                        className={`px-4 py-2 rounded-full text-sm transition-all ${
                          occasion === o
                            ? 'bg-brand-forest text-brand-bg'
                            : 'bg-brand-bg2 text-brand-ink2 hover:bg-brand-line'
                        }`}
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="msg" className="text-brand-ink2 mb-2 block text-xs tracking-widest uppercase">Your message (optional)</Label>
                  <Textarea
                    id="msg"
                    data-testid="input-message"
                    rows={3}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="A line from you, engraved beside their tree…"
                    className="bg-brand-bg2 border-brand-line rounded-xl"
                  />
                </div>
              </div>

              {/* Summary pills */}
              <div className="mt-6 flex flex-wrap gap-2">
                <div className="inline-flex items-center gap-2 rounded-full bg-brand-bg2 border border-brand-line px-3 py-1.5 text-xs text-brand-ink2">
                  <span>{planObj.icon}</span> {planObj.name} · {formatINR(planObj.price)}
                </div>
                <div className="inline-flex items-center gap-2 rounded-full bg-brand-bg2 border border-brand-line px-3 py-1.5 text-xs text-brand-ink2">
                  🌿 {treeObj.name}
                </div>
              </div>

              {/* Add-ons — offered only at checkout */}
              <div className="mt-7 rounded-2xl border border-brand-line bg-brand-bg2 p-5" data-testid="checkout-addons">
                <div className="flex items-baseline justify-between mb-4">
                  <div>
                    <div className="text-brand-gold tracking-[0.25em] uppercase text-[10px] mb-1">Soulful add-ons · optional</div>
                    <div className="font-serif text-lg text-brand-ink leading-tight">Small touches, deeper roots</div>
                  </div>
                  <div className="text-[11px] text-brand-ink2 italic">Add to cart below</div>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  {ADDONS.map((a) => {
                    const selected = addonKeys.includes(a.key);
                    return (
                      <button
                        key={a.key}
                        type="button"
                        onClick={() => toggleAddon(a.key)}
                        data-testid={`addon-toggle-${a.key}`}
                        className={`text-left rounded-xl p-4 border transition-all ${
                          selected
                            ? 'border-brand-forest ring-2 ring-brand-forest/25 bg-brand-bg'
                            : 'border-brand-line bg-brand-bg hover:border-brand-gold/60'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-1.5">
                          <div className="font-serif text-[15px] text-brand-ink leading-tight">{a.name}</div>
                          <div className={`w-4 h-4 rounded-full border flex-shrink-0 flex items-center justify-center ${selected ? 'bg-brand-forest border-brand-forest' : 'border-brand-line'}`}>
                            {selected && <div className="w-1.5 h-1.5 rounded-full bg-brand-bg" />}
                          </div>
                        </div>
                        <div className="text-brand-ink2 text-[12px] leading-relaxed mb-2">{a.copy}</div>
                        <div className="text-brand-forest font-serif text-sm">
                          +{formatINR(a.price)}
                          {a.unit && <span className="text-[10px] tracking-widest uppercase text-brand-ink2 ml-1">/ {a.unit}</span>}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <div className="mt-4 pt-4 border-t border-brand-line flex items-center justify-between text-[13px]">
                  <span className="text-brand-ink2">Total</span>
                  <span className="font-serif text-xl text-brand-ink" data-testid="checkout-grand-total">{formatINR(grandTotal)}</span>
                </div>
              </div>

              {!payConfig.configured && (
                <div className="mt-4 flex items-start gap-2.5 rounded-xl bg-brand-gold/10 border border-brand-gold/30 px-4 py-3 text-[12.5px] text-brand-ink2" data-testid="payment-preview-banner">
                  <AlertCircle className="w-4 h-4 mt-0.5 text-brand-gold flex-shrink-0" />
                  <span>Payment gateway not yet configured — proceeding will show a <strong>preview certificate</strong>. Add your Razorpay keys to enable real checkout.</span>
                </div>
              )}

              <div className="mt-8 flex items-center justify-between">
                <button
                  onClick={() => setStep(1)}
                  data-testid="dialog-back-details-button"
                  className="text-brand-ink2 hover:text-brand-ink text-sm"
                >
                  ← Back
                </button>
                <button
                  onClick={handlePayAndGenerate}
                  disabled={!canProceed || paying}
                  data-testid="dialog-submit-button"
                  className={`inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm tracking-wide font-medium transition-all ${
                    canProceed && !paying
                      ? 'bg-brand-forest text-brand-bg hover:bg-brand-ink'
                      : 'bg-brand-line text-brand-ink2/60 cursor-not-allowed'
                  }`}
                >
                  {paying ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Opening secure checkout…</>
                  ) : payConfig.configured ? (
                    <>Pay {formatINR(grandTotal)} & Adopt →</>
                  ) : (
                    <>Generate preview certificate →</>
                  )}
                </button>
              </div>
            </motion.div>
          )}

          {step === 3 && (
            <motion.div
              key="step-thanks"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.6 }}
              className="px-8 py-10"
              data-testid="thank-you-screen"
            >
              <div className="text-center mb-7">
                <div className="inline-flex items-center gap-2 text-brand-gold text-xs tracking-[0.25em] uppercase mb-3">
                  <Sparkles className="w-3.5 h-3.5" /> Dedication received
                </div>
                <h3 className="font-serif text-3xl md:text-4xl text-brand-ink font-light leading-tight">
                  Thank you. <em className="text-brand-forest">A tree is on its way.</em>
                </h3>
                <p className="text-brand-ink2 mt-3 max-w-md mx-auto text-[15px] leading-relaxed">
                  Your {treeObj.name} tree will be planted under the <em>{planObj.name}</em> plan with {recipient}'s name within 7 days.
                </p>
              </div>

              <div
                className="certificate-bg grain-overlay relative rounded-2xl border border-brand-gold/30 p-8 md:p-10 mx-auto max-w-xl"
                data-testid="certificate-preview"
              >
                <div className="text-center">
                  <div className="inline-flex items-center gap-2 text-brand-forest mb-4">
                    <Leaf className="w-4 h-4" />
                    <span className="font-serif tracking-[0.3em] text-xs uppercase">Pallavit Orchard</span>
                    <Leaf className="w-4 h-4 scale-x-[-1]" />
                  </div>
                  <div className="text-brand-ink2 text-[11px] tracking-[0.3em] uppercase mb-4">Certificate of Dedication</div>
                  <div className="text-brand-ink2 font-serif italic text-sm mb-1">This is to honour the planting of a</div>
                  <div className="font-serif text-4xl md:text-5xl text-brand-forest font-medium mb-1">{treeObj.name} Tree</div>
                  <div className="text-brand-gold italic font-serif text-sm mb-6">{treeObj.local}</div>

                  <div className="leaf-divider text-[10px] tracking-[0.3em] uppercase mb-6">Dedicated to</div>

                  <div className="font-serif text-3xl md:text-4xl text-brand-ink mb-4">{recipient}</div>

                  {message && (
                    <div className="font-serif italic text-brand-ink2 text-base md:text-lg max-w-sm mx-auto mb-6 leading-relaxed">
                      "{message}"
                    </div>
                  )}

                  <div className="text-brand-ink2 text-sm mb-1">On the occasion of <span className="text-brand-ink">{occasion}</span></div>
                  <div className="text-brand-ink2 text-sm mb-4">Gifted with love by <span className="text-brand-ink">{senderName}</span></div>

                  <div className="inline-flex items-center gap-2 rounded-full bg-brand-forest/10 border border-brand-forest/25 px-4 py-1.5 text-[11px] tracking-widest uppercase text-brand-forest">
                    <span>{planObj.icon}</span>{planObj.name} · {planObj.duration}
                  </div>

                  <div className="mt-7 pt-5 border-t border-brand-gold/30 flex justify-between items-center text-[11px] text-brand-ink2 tracking-widest uppercase">
                    <span>Kalwan Taluka, Nashik</span>
                    <span>Ref · {certificateId}</span>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                {orderId ? (
                  <a
                    href={`${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/certificate.pdf`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="certificate-pdf-download"
                    className="rounded-full border border-brand-forest text-brand-forest hover:bg-brand-forest hover:text-brand-bg px-6 py-3 text-sm tracking-wide font-medium transition-all inline-flex items-center gap-2 justify-center"
                  >
                    <Download className="w-4 h-4" /> Download PDF certificate
                  </a>
                ) : (
                  <button
                    onClick={() => window.print()}
                    data-testid="certificate-save-button"
                    className="rounded-full border border-brand-forest text-brand-forest hover:bg-brand-forest hover:text-brand-bg px-6 py-3 text-sm tracking-wide font-medium transition-all inline-flex items-center gap-2 justify-center"
                  >
                    <Download className="w-4 h-4" /> Save / Print certificate
                  </button>
                )}
                <button
                  onClick={() => onOpenChange(false)}
                  data-testid="dialog-close-button"
                  className="rounded-full bg-brand-forest text-brand-bg px-6 py-3 text-sm tracking-wide font-medium hover:bg-brand-ink transition-all inline-flex items-center gap-2 justify-center"
                >
                  <GiftIcon className="w-4 h-4" /> Done
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <button
          onClick={() => onOpenChange(false)}
          aria-label="Close"
          data-testid="dialog-x-button"
          className="absolute top-4 right-4 w-9 h-9 rounded-full bg-brand-bg2 hover:bg-brand-line flex items-center justify-center text-brand-ink z-20"
        >
          <X className="w-4 h-4" />
        </button>
      </DialogContent>
    </Dialog>
  );
}
